import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Chip,
  Typography,
} from '@material-tailwind/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactToPrint from 'react-to-print';
import AlertMessage from '../../common/actions/AlertMessage';
import Loader from '../../common/loader/Loader';
import DetailItem from '../../common/shared/DetailItem';
import SectionTitle from '../../common/shared/SectionTitle';
import { handleApi } from '../../redux/api/utils';
import './summit.css';

const money = value => `GH₵${Number(value || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 })}`;
const statusLabel = status => ({ 0: 'Pending', 1: 'Paid', 3: 'Unsuccessful', 4: 'Needs reconciliation' }[status] || 'Pending');

function SummitStatusTag({ paid, receipt }) {
  const status = paid ? 'Confirmed' : receipt ? statusLabel(receipt.status) : 'Open for registration';
  const color = paid ? 'green' : receipt?.status === 3 ? 'red' : receipt?.status === 4 ? 'amber' : 'light-blue';

  return (
    <Chip
      variant="ghost"
      color={color}
      value={status}
      className="mb-4 w-fit"
    />
  );
}

function PaymentDocument({ receipt, product, user }) {
  const ref = useRef(null);
  const isReceipt = receipt.status === 1;

  return (
    <Card className="mb-4">
      <CardBody>
        <div ref={ref} className="summit-print">
          <p>GHANA BAR ASSOCIATION</p>
          <h2>{isReceipt ? 'Payment Receipt' : 'Payment Invoice'}</h2>
          <h3>{product.name}</h3>
          <p>October 7, 2026 · 8:30am · Labadi Beach Hotel</p>
          <p>{[user?.title, user?.first_name, user?.last_name].filter(Boolean).join(' ')} · {receipt.bar_number}</p>
          <p>Document ID: {receipt.id}</p>
          <p>Status: <strong>{statusLabel(receipt.status)}</strong> · {receipt.method === 1 ? 'Electronic payment' : 'Manual payment'}</p>
          <dl>
            <dt>Ticket</dt>
            <dd>{money(receipt.amount)}</dd>
            <dt>Payment charge</dt>
            <dd>{money(receipt.charges)}</dd>
            <dt>Total</dt>
            <dd><strong>{money(Number(receipt.amount) + Number(receipt.charges))}</strong></dd>
          </dl>
          <p>Reference: {receipt.notes}</p>
          {receipt.date_paid && <p>Payment date: {new Date(receipt.date_paid).toLocaleString('en-GB', { timeZone: 'Africa/Accra' })} (Ghana time)</p>}
          {receipt.method === 0 && receipt.status === 0 && (
            <p>
              Present this invoice and make payment at the Bar Secretariat: H/No. 5, 2nd Ave.,
              State House Road, South-East Ridge, adjacent to Ghana International Press Centre,
              Accra. Your payment remains pending until Secretariat confirmation. This invoice
              is not proof of payment.
            </p>
          )}
        </div>
      </CardBody>
      <CardFooter>
        <ReactToPrint
          trigger={() => <Button variant="outlined">Print {isReceipt ? 'receipt' : 'invoice'}</Button>}
          content={() => ref.current}
        />
      </CardFooter>
    </Card>
  );
}

export default function Summit() {
  const user = useSelector(state => state.auth?.userData);
  const location = useLocation();
  const routePrefix = location.pathname.startsWith('/mobile') ? '/mobile' : '/home';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const returnChecked = useRef(false);

  const load = useCallback(async () => {
    const result = await handleApi('get', '/portal/summit');
    if (result.error) {
      setError(result.error);
    } else {
      setData(result.data);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get('reference') || params.get('trxref');
    const receipt = data?.receipts.find(r => r.notes === reference && r.method === 1);

    if (!receipt || returnChecked.current || receipt.status === 1 || receipt.status === 4) return;

    returnChecked.current = true;
    setBusy(true);
    handleApi('post', `/portal/summit/payments/${receipt.id}/verify`, {})
      .then(async result => {
        if (result.error) {
          setError(result.error);
        } else {
          setMessage(`Payment status: ${statusLabel(result.data.receipt.status)}.`);
          await load();
        }
      })
      .finally(() => setBusy(false));
  }, [data, load]);

  const act = async (path, redirect = false) => {
    setBusy(true);
    setError('');
    setMessage('');

    try {
      const payload = path === 'electronic' ? { mobile: location.pathname.startsWith('/mobile/') } : {};
      const result = await handleApi('post', `/portal/summit/${path}`, payload);

      if (result.error) {
        setError(result.error);
        await load();
        return;
      }

      const receipt = result.data.receipt;

      if (redirect && receipt.status === 0 && receipt.url) {
        window.location.assign(receipt.url);
        return;
      }

      setMessage(
        receipt.status === 1
          ? 'Your summit ticket is confirmed.'
          : receipt.status === 4
            ? 'An additional payment needs Secretariat reconciliation. Please contact the Secretariat.'
            : path === 'manual'
              ? 'Your manual invoice is ready. Complete payment at the Secretariat.'
              : `Payment status: ${statusLabel(receipt.status)}.`
      );
      await load();
    } finally {
      setBusy(false);
    }
  };

  const paid = data?.receipts.some(r => r.status === 1);
  const manual = data?.receipts.find(r => r.method === 0 && r.status === 0);
  const latestReceipt = data?.receipts[0];

  return (
    <>
      <SectionTitle
        title="Boardroom Governance Summit 2026"
        description="Note that you have to be in good financial standing for 2026 national dues in order to register for the summit."
        children={[{ name: 'Boardroom Governance Summit', link: `${routePrefix}/summit` }]}
        link={routePrefix}
      />

      <main className="my-8 max-w-[1200px] m-auto px-4">
        {error && <AlertMessage type="error" message={error} />}
        {message && (
          <div role="status">
            <AlertMessage type="success" message={message} />
          </div>
        )}

        {!data ? (
          error ? (
            <Button variant="outlined" onClick={load}>Refresh</Button>
          ) : (
            <Loader />
          )
        ) : (
          <>
            <div className="card-wrapper">
              <Card>
                <CardBody>
                  <SummitStatusTag paid={paid} receipt={latestReceipt} />
                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 items-start">
                    <div>
                      <Typography variant="h5" color="blue-gray" className="mb-4">
                        {data.product.name}
                      </Typography>
                      <DetailItem
                        title="Theme"
                        item="Board Committees: From Oversight to Impact. The Conversation Every Board Must Have."
                      />
                      <DetailItem title="Venue" item="Labadi Beach Hotel" />
                      <DetailItem title="Date" item="Wednesday, 07 October, 2026 at 8:30am" />
                      <DetailItem title="Ticket Amount" item={money(data.product.amount)} />
                      <DetailItem
                        title="Electronic Payment"
                        item={(
                          <>
                            <span>{money(data.electronic.total)}</span>
                            <br />
                            <span className="text-base">Includes {money(data.electronic.charges)} charge</span>
                          </>
                        )}
                      />
                      <DetailItem
                        title="Manual Payment"
                        item={(
                          <>
                            <span>{money(data.manual.total)}</span>
                            <br />
                            <span className="text-base">Includes {money(data.manual.charges)} charge</span>
                          </>
                        )}
                      />
                      <DetailItem
                        title="Sales Deadline"
                        item="Sales close at the end of October 6, 2026 (Ghana time)."
                      />
                    </div>
                    <img
                      className="summit-flyer"
                      src="/images/boardroom-summit-2026.jpg"
                      alt="Boardroom Governance Summit 2026 flyer. Ticket price GH₵2,500."
                    />
                  </div>
                </CardBody>
                <CardFooter>
                  {paid ? (
                    <AlertMessage type="success" message="Your ticket is paid. Your receipt is available below." />
                  ) : !data.sales_open ? (
                    <AlertMessage type="warning" message="Ticket sales have closed. Existing payments can still be verified below." />
                  ) : !data.eligible ? (
                    <AlertMessage
                      type="warning"
                      message={(
                        <>
                          Paid 2026 national dues or a recorded dues exemption is required.{' '}
                          <Link className="underline font-bold" to={`${routePrefix}/dues`}>Pay your dues</Link>.
                        </>
                      )}
                    />
                  ) : (
                    <div className="flex flex-col gap-3 md:flex-row md:items-center">
                      <Button disabled={busy} color="pink" onClick={() => act('electronic', true)}>
                        {busy ? 'Please wait…' : 'Pay electronically'}
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={busy || Boolean(manual)}
                        onClick={() => act('manual')}
                      >
                        {manual ? 'Manual invoice created' : 'Create manual invoice'}
                      </Button>
                    </div>
                  )}
                  <Typography variant="small" className="mt-4 block">
                    One ticket per member. If you have already attempted electronic payment, verify it below before making another payment.
                  </Typography>
                </CardFooter>
              </Card>
            </div>

            <section className="mt-10">
              <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <Typography variant="h5" color="blue-gray">Your summit payments</Typography>
                <Button disabled={busy} variant="outlined" onClick={load}>Refresh payment status</Button>
              </div>

              {!data.receipts.length && (
                <Typography color="blue-gray">No payment started yet.</Typography>
              )}

              {data.receipts.map(receipt => (
                <div key={receipt.id}>
                  <PaymentDocument receipt={receipt} product={data.product} user={user} />
                  {receipt.method === 1 && receipt.status !== 1 && receipt.status !== 4 && (
                    <Button disabled={busy} color="pink" onClick={() => act(`payments/${receipt.id}/verify`)}>
                      Verify electronic payment
                    </Button>
                  )}
                  {receipt.status === 4 && (
                    <div role="status">
                      <AlertMessage
                        type="warning"
                        message="An additional payment was received. Contact the Secretariat for reconciliation; a second ticket has not been issued."
                      />
                    </div>
                  )}
                </div>
              ))}
            </section>
          </>
        )}
      </main>
    </>
  );
}
